import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { VAULTS } from "../data";
import { Chip, Label, MetaRow, Reveal, StatusDot, ComingSoonBox } from "../components/primitives";

const StatBox = ({ k, v, soon }) => (
  <div className="border border-white/[0.08] bg-panel p-5" data-testid={`vault-stat-${k.toLowerCase().replace(/\s/g, "-")}`}>
    <Label>{k}</Label>
    <div className={`mt-2 font-mono text-xl font-bold tracking-wide ${soon ? "text-brass-bright" : "text-fog-100"}`}>{v}</div>
  </div>
);

export default function Vaults() {
  const [open, setOpen] = useState(null);

  return (
    <main className="pt-16" data-testid="vaults-page">
      <div className="grid-bg border-b border-white/[0.06]">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-20">
          <Reveal>
            <div className="flex items-center gap-3"><StatusDot /><Label tone="green">Programmable capital under explicit constraints</Label></div>
            <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-fog-100 sm:text-5xl">AUTONOMOUS VAULTS</h1>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-fog-400 sm:text-base">
              Programmable capital managed by intelligent agents under explicit, contract-enforced constraints.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-[1440px] px-5 py-10 md:px-8">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatBox k="Total Vaults" v="COMING SOON" soon />
          <StatBox k="Total TVL" v="COMING SOON" soon />
          <StatBox k="Active Agents" v="COMING SOON" soon />
          <StatBox k="Network" v="ROBINHOOD CHAIN" />
        </div>

        <div className="mt-10 grid gap-px border border-white/[0.08] bg-white/[0.08] md:grid-cols-2">
          {VAULTS.map((v, i) => (
            <Reveal key={v.name} delay={i * 0.06}>
              <div className="group h-full bg-panel p-6 transition-colors duration-300 hover:bg-elevated" data-testid={`vault-card-${v.name.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-display text-lg font-bold tracking-wide text-fog-100">{v.name}</h3>
                    <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-fog-600">{v.profile}</span>
                  </div>
                  <Chip tone="gold">Coming Soon</Chip>
                </div>
                <div className="mt-6">
                  <MetaRow k="Capital" v="COMING SOON" />
                  <MetaRow k="Allocation" v={v.allocation} mono={false} />
                  <MetaRow k="Risk" v={v.risk} />
                  <MetaRow k="Agent" v={v.agent} />
                  <MetaRow k="Status" v="STANDBY" />
                </div>
                <button
                  onClick={() => setOpen(v)}
                  data-testid={`vault-view-${v.name.toLowerCase().replace(/\s/g, "-")}`}
                  className="mt-6 w-full border border-white/15 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-fog-100 transition-all hover:border-pulse/50 hover:text-pulse-soft"
                >
                  View Vault
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-fog-600">
          Demo vaults — no real TVL or performance is represented.
        </p>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-end justify-center bg-ink-900/80 backdrop-blur-sm sm:items-center sm:p-6"
            onClick={() => setOpen(null)}
            data-testid="vault-modal-backdrop"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg border border-white/[0.1] bg-panel"
              data-testid="vault-modal"
            >
              <div className="flex items-center justify-between border-b border-white/[0.08] px-6 py-4">
                <h3 className="font-display text-lg font-bold tracking-wide text-fog-100">{open.name}</h3>
                <button onClick={() => setOpen(null)} data-testid="vault-modal-close"
                  className="flex h-9 w-9 items-center justify-center border border-white/10 text-fog-400 hover:text-fog-100">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="p-6">
                <MetaRow k="Profile" v={open.profile} />
                <MetaRow k="Risk" v={open.risk} />
                <MetaRow k="Managing Agent" v={open.agent} />
                <MetaRow k="Allocation" v={open.allocation} mono={false} />
                <ComingSoonBox label="Deposits / Withdrawals" className="mt-6" />
                <ComingSoonBox label="Onchain Vault Contract" className="mt-3" />
                <p className="mt-5 text-[11px] leading-relaxed text-fog-600">
                  Vault contracts are not yet deployed. Deposit, withdrawal and performance interfaces will
                  activate with the Capital phase of the roadmap.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
